import ky, { type KyInstance } from 'ky';
import type { AuthAdapter } from './auth-adapter';
import { createSingleFlightRefresh } from './refresh';

export interface CreateApiClientOptions {
  prefixUrl: string;
  authAdapter: AuthAdapter;
}

export function createApiClient({ prefixUrl, authAdapter }: CreateApiClientOptions): KyInstance {
  const refresh = createSingleFlightRefresh<string | null>(async () => {
    try {
      const res = await ky
        .post('auth/refresh', { prefixUrl, credentials: 'include' })
        .json<{ accessToken: string }>();
      authAdapter.setAccess(res.accessToken);
      return res.accessToken;
    } catch {
      authAdapter.onRefreshFail();
      return null;
    }
  });

  return ky.create({
    prefixUrl,
    credentials: 'include',
    retry: { limit: 0 },
    hooks: {
      beforeRequest: [
        (req) => {
          const access = authAdapter.getAccess();
          if (access) req.headers.set('Authorization', `Bearer ${access}`);
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          if (response.status !== 401) return response;
          // Avoid recursion on the refresh endpoint itself.
          if (request.url.endsWith('/auth/refresh')) return response;
          // Гард: если access ещё не выставлен, мы в first-call'е до initial-auth
          // (mini-app: запрос полетел раньше initData-exchange'а; admin: до login-widget).
          // Refresh без сессии гарантированно вернёт 401 → onRefreshFail-навигация
          // конфликтовала бы с auth-bootstrap'ом в route beforeLoad. Пробрасываем 401
          // как есть: сессию создаёт exchange/login-widget, а не автоматический ретрай
          // этого же запроса — в mini-app react-query 401/403 сознательно исключены из
          // retry (`isAuthRetryBlockedError` в apps/mini-app/src/app/query-client.ts),
          // доступ восстанавливает явный `ensureQueryData` ПОСЛЕ exchange() в beforeLoad.
          if (!authAdapter.getAccess()) return response;

          const newAccess = await refresh();
          if (!newAccess) return response;

          const retried = new Request(request, {
            headers: new Headers(request.headers),
          });
          retried.headers.set('Authorization', `Bearer ${newAccess}`);
          // Cast: ky's Options has many `T | undefined` fields that clash with
          // exactOptionalPropertyTypes when spread. Behaviour is unchanged.
          return ky(retried, { ...options, hooks: {}, retry: { limit: 0 } } as Parameters<typeof ky>[1]);
        },
      ],
    },
  });
}
